import { createAsyncThunk } from '@reduxjs/toolkit';
import apiCall from '../../utils/api';

const getErrorMessage = (error: any, defaultMessage: string) => {
  if (error.response?.data?.data) {
    const errorData = error.response.data.data;
    let errors: string[] = [];
    if (typeof errorData === 'object' && errorData !== null) {
      for (const key in errorData) {
        if (Object.prototype.hasOwnProperty.call(errorData, key)) {
          const messages = errorData[key];
          if (Array.isArray(messages)) {
            errors.push(`${key}: ${messages.join(', ')}`);
          } else {
            errors.push(`${key}: ${messages}`);
          }
        }
      }
    }

    if (errors.length > 0) {
      return errors.join('\n');
    }
  }
  return error.response?.data?.message || defaultMessage;
};

// Roles
export const fetchSystemRoles = createAsyncThunk(
  'superadmin/fetchSystemRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('get', '/api/superadmin/system-role/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch system roles'));
    }
  }
);

export const createSystemRole = createAsyncThunk(
  'superadmin/createSystemRole',
  async (payload: { role_name: string; role_description: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/system-role/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create system role'));
    }
  }
);

export const updateSystemRole = createAsyncThunk(
  'superadmin/updateSystemRole',
  async (payload: { role_id: number; role_name: string; role_description: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/system-role/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update system role'));
    }
  }
);

export const deleteSystemRole = createAsyncThunk(
  'superadmin/deleteSystemRole',
  async (role_id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', '/api/superadmin/system-role/', { role_id });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete system role'));
    }
  }
);

// Modules
export const fetchSystemModules = createAsyncThunk(
  'superadmin/fetchSystemModules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('get', '/api/superadmin/system-module/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch system modules'));
    }
  }
);

export const createSystemModule = createAsyncThunk(
  'superadmin/createSystemModule',
  async (payload: { module_name: string; module_slug: string; module_description: string; module_type: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/system-module/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create system module'));
    }
  }
);

export const updateSystemModule = createAsyncThunk(
  'superadmin/updateSystemModule',
  async (payload: { module_id: number; module_name?: string; module_slug?: string; module_description?: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/system-module/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update system module'));
    }
  }
);

export const deleteSystemModule = createAsyncThunk(
  'superadmin/deleteSystemModule',
  async (module_id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', '/api/superadmin/system-module/', { module_id });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete system module'));
    }
  }
);

// Mappings
export const fetchRoleModuleMappings = createAsyncThunk(
  'superadmin/fetchRoleModuleMappings',
  async (system_role_id: number | undefined, { rejectWithValue }) => {
    try {
      const url = system_role_id ? `/api/superadmin/role-module-mapping/?system_role_id=${system_role_id}` : '/api/superadmin/role-module-mapping/';
      const response = await apiCall<any>('get', url);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch mappings'));
    }
  }
);

export const createRoleModuleMapping = createAsyncThunk(
  'superadmin/createRoleModuleMapping',
  async (payload: {
    system_role_id: number;
    system_module_id: number;
    have_admin?: boolean;
    have_read?: boolean;
    have_write?: boolean;
    have_delete?: boolean;
    have_edit?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/role-module-mapping/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create mapping'));
    }
  }
);

export const updateRoleModuleMapping = createAsyncThunk(
  'superadmin/updateRoleModuleMapping',
  async (payload: {
    mapping_id: number;
    have_admin?: boolean;
    have_read?: boolean;
    have_write?: boolean;
    have_delete?: boolean;
    have_edit?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/role-module-mapping/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update mapping'));
    }
  }
);

export const deleteRoleModuleMapping = createAsyncThunk(
  'superadmin/deleteRoleModuleMapping',
  async (mapping_id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', '/api/superadmin/role-module-mapping/', { mapping_id });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete mapping'));
    }
  }
);

// User Mappings
export const fetchUserRoleMappings = createAsyncThunk(
  'superadmin/fetchUserRoleMappings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('get', '/api/superadmin/user-role-mapping/');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch user mappings'));
    }
  }
);

export const createUserWithMapping = createAsyncThunk(
  'superadmin/createUserWithMapping',
  async (payload: { email: string; first_name: string; last_name: string; phone_number: string; system_role_id: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/create-user-with-mapping/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create user'));
    }
  }
);

export const deleteUserRoleMapping = createAsyncThunk(
  'superadmin/deleteUserRoleMapping',
  async (mappingId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', '/api/superadmin/user-role-mapping/', { mapping_id: mappingId });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete user mapping'));
    }
  }
);

export const updateUserRoleMapping = createAsyncThunk(
  'superadmin/updateUserRoleMapping',
  async (data: { mapping_id: number; system_role_id: number }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/user-role-mapping/', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update user role'));
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'superadmin/updateUserStatus',
  async (data: { user_id: number; action: 'activate' | 'deactivate' | 'suspend'; remark?: string }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/user-status/', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, `Failed to ${data.action} user`));
    }
  }
);

// Document Types
export const fetchSuperAdminDocumentTypes = createAsyncThunk(
  'superadmin/fetchDocumentTypes',
  async (params: { search?: string; page?: number; page_size?: number } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
      
      const response = await apiCall<any>('get', `/api/superadmin/document-types/?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch document types'));
    }
  }
);

export const createSuperAdminDocumentType = createAsyncThunk(
  'superadmin/createDocumentType',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/document-types/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create document type'));
    }
  }
);

export const updateSuperAdminDocumentType = createAsyncThunk(
  'superadmin/updateDocumentType',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/document-types/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update document type'));
    }
  }
);

export const deleteSuperAdminDocumentType = createAsyncThunk(
  'superadmin/deleteDocumentType',
  async (document_type_id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/api/superadmin/document-types/?document_type_id=${document_type_id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete document type'));
    }
  }
);

// Signup Fields
export const fetchSuperAdminSignupFields = createAsyncThunk(
  'superadmin/fetchSignupFields',
  async (params: { is_active?: boolean; page?: number; page_size?: number } | undefined, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
      
      const response = await apiCall<any>('get', `/api/superadmin/signup-fields/?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch signup fields'));
    }
  }
);

export const createSuperAdminSignupField = createAsyncThunk(
  'superadmin/createSignupField',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/superadmin/signup-fields/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create signup field'));
    }
  }
);

export const updateSuperAdminSignupField = createAsyncThunk(
  'superadmin/updateSignupField',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('patch', '/api/superadmin/signup-fields/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update signup field'));
    }
  }
);

export const deleteSuperAdminSignupField = createAsyncThunk(
  'superadmin/deleteSignupField',
  async (field_id: number, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('delete', `/api/superadmin/signup-fields/?field_id=${field_id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete signup field'));
    }
  }
);
export const updateOrganizationSignupDocumentStatus = createAsyncThunk(
  'superadmin/updateOrganizationSignupDocumentStatus',
  async (payload: { 
    document_id: number; 
    action?: 'accept' | 'reject'; 
    remarks?: string;
    issue_date?: string;
    expiry_date?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await apiCall<any>('post', '/api/organization/superadmin/signup-document-status/', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update document status'));
    }
  }
);
